import * as React from 'react';
import { useRef, useState, useCallback, useEffect } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface ISlideToConfirmProps {
  onConfirm: () => void;
  label: string;
  disabled?: boolean;
  confirmed?: boolean;
}

const THUMB_SIZE = 48;
const TRACK_HEIGHT = 52;
const CONFIRM_THRESHOLD = 0.85;

const useStyles = makeStyles(() =>
  createStyles({
    track: {
      position: 'relative',
      height: TRACK_HEIGHT,
      borderRadius: TRACK_HEIGHT / 2,
      backgroundColor: '#1976d2',
      overflow: 'hidden',
      userSelect: 'none',
      touchAction: 'none',
    },
    trackDisabled: {
      backgroundColor: '#bdbdbd',
    },
    label: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 500,
      fontSize: '0.95rem',
      pointerEvents: 'none',
      transition: 'opacity 0.2s',
    },
    thumb: {
      position: 'absolute',
      top: (TRACK_HEIGHT - THUMB_SIZE) / 2,
      left: 2,
      width: THUMB_SIZE,
      height: THUMB_SIZE,
      borderRadius: '50%',
      backgroundColor: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      cursor: 'grab',
      transition: 'none',
      zIndex: 2,
    },
    thumbReset: {
      transition: 'left 0.3s ease',
    },
    confirmed: {
      backgroundColor: '#4caf50',
    },
  })
);

const SlideToConfirm: React.FC<ISlideToConfirmProps> = ({
  onConfirm,
  label,
  disabled = false,
  confirmed,
}): JSX.Element => {
  const classes = useStyles({});
  const trackRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const startXRef = useRef(0);
  const maxOffsetRef = useRef(0);

  useEffect(() => {
    if (confirmed === false && isConfirmed) {
      setIsConfirmed(false);
      setOffset(0);
    }
  }, [confirmed, isConfirmed]);

  const getMaxOffset = useCallback(() => {
    if (!trackRef.current) return 0;
    return trackRef.current.offsetWidth - THUMB_SIZE - 4;
  }, []);

  const handleStart = useCallback(
    (clientX: number) => {
      if (disabled || isConfirmed) return;
      startXRef.current = clientX - offset;
      maxOffsetRef.current = getMaxOffset();
      setIsDragging(true);
    },
    [disabled, isConfirmed, offset, getMaxOffset]
  );

  const handleMove = useCallback(
    (clientX: number) => {
      if (!isDragging) return;
      const newOffset = Math.min(
        Math.max(0, clientX - startXRef.current),
        maxOffsetRef.current
      );
      setOffset(newOffset);
    },
    [isDragging]
  );

  const handleEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    const max = maxOffsetRef.current;
    if (max > 0 && offset / max >= CONFIRM_THRESHOLD) {
      setOffset(max);
      setIsConfirmed(true);
      onConfirm();
    } else {
      setOffset(0);
    }
  }, [isDragging, offset, onConfirm]);

  const onTouchStart = (e: React.TouchEvent) =>
    handleStart(e.touches[0].clientX);
  const onTouchMove = (e: React.TouchEvent) =>
    handleMove(e.touches[0].clientX);
  const onMouseDown = (e: React.MouseEvent) => handleStart(e.clientX);

  React.useEffect(() => {
    if (!isDragging) return;
    const onUp = () => handleEnd();
    const onMove = (e: MouseEvent) => handleMove(e.clientX);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mousemove', onMove);
    };
  }, [isDragging, handleEnd, handleMove]);

  const progress = maxOffsetRef.current > 0 ? offset / maxOffsetRef.current : 0;

  return (
    <div
      ref={trackRef}
      className={`${classes.track} ${disabled ? classes.trackDisabled : ''} ${
        isConfirmed ? classes.confirmed : ''
      }`}
      onTouchMove={onTouchMove}
      onTouchEnd={handleEnd}
    >
      <Typography
        className={classes.label}
        style={{ opacity: isConfirmed ? 0 : 1 - progress * 0.8 }}
      >
        {isConfirmed ? '' : label}
      </Typography>
      <div
        className={`${classes.thumb} ${!isDragging ? classes.thumbReset : ''}`}
        style={{ left: offset + 2 }}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        <ChevronRightIcon style={{ color: '#1976d2', fontSize: '1.8rem' }} />
      </div>
    </div>
  );
};

export default SlideToConfirm;
